import { lazy } from "react";
import MyAssessmentIcon from "@assets/SVGIcons/MyAssessmentIcon.web";
import MyCoursesIcon from "@assets/SVGIcons/MyCoursesIcon.web";
import MyGradebookIcon from "@assets/SVGIcons/MyGradebookIcon.web";
import MyPathIcon from "@assets/SVGIcons/MyPathIcon.web";
import ReadIcon from "@assets/SVGIcons/ReadIcon.web";
import ReportCardIcon from "@assets/SVGIcons/ReportCardIcon.web";
import { NavigationGroup, NavigationRoute } from ".";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DashboardIcon from "@assets/SVGIcons/DashboardIcon.web";
import { Navigate } from "react-router-dom";

// LAZY ROUTES
const Profile = lazy(() => import("@components/pages/parentTeacherWorkSpace/profile/ParentTeacherProfile"));
const EditProfile = lazy(() => import("@components/pages/shared/profile/EditProfile"));
const ChangePassword = lazy(() => import("@components/pages/shared/profile/ChangePassword"));
const Calendar = lazy(() => import("@components/pages/parentTeacherWorkSpace/calendar/ParentTeacherCalendar"));

const Dashboard = lazy(() => import("@components/pages/parentTeacherWorkSpace/dashboard/Dashboard"));
const DashboardWidget = lazy(() => import("@components/pages/parentTeacherWorkSpace/dashboard/DashboardWidget"));

const Students = lazy(() => import("@components/pages/parentTeacherWorkSpace/students/Students"));
const StudentProfile = lazy(() => import("@components/pages/parentTeacherWorkSpace/students/StudentProfile"));

const Courses = lazy(() => import("@components/pages/parentTeacherWorkSpace/courses/Courses"));
const CoursesCoursesTab = lazy(() => import("@components/pages/parentTeacherWorkSpace/courses/tabs/CoursesTab"));
const CoursesJourneyTab = lazy(() => import("@components/pages/parentTeacherWorkSpace/courses/tabs/JourneyTab"));

const Assessments = lazy(() => import("@components/pages/parentTeacherWorkSpace/assessment/Assessments"));
const AssessmentsAssignedTab = lazy(() => import("@components/pages/parentTeacherWorkSpace/assessment/tabs/AssessmentsAssignedTab"));
const AssessmentsGradedTab = lazy(() => import("@components/pages/parentTeacherWorkSpace/assessment/tabs/AssessmentsGradedTab"));

const GradeBooks = lazy(() => import("@components/pages/parentTeacherWorkSpace/gradebooks/GradeBooks"));
const GradeBookDetailsRoute = lazy(() => import("@components/pages/parentTeacherWorkSpace/gradebooks/GradeBookDetailsRoute"));
const GradeBookGradesTab = lazy(() => import("@components/pages/parentTeacherWorkSpace/gradebooks/tabs/GradeBookGradesTab"));
const GradeBookAttendancesTab = lazy(() => import("@components/pages/parentTeacherWorkSpace/gradebooks/tabs/GradeBookAttendanceTab"));

const CourseManagement = lazy(() => import("@components/pages/parentTeacherWorkSpace/course-management/CourseManagement"));
const CourseManagementTeacherCourses = lazy(() => import("@components/pages/parentTeacherWorkSpace/course-management/tabs/TeacherCourses"));
const CourseManagementCatalogs = lazy(() => import("@components/pages/parentTeacherWorkSpace/course-management/tabs/Catalogs"));
const CourseManagementCatalogCourses = lazy(() => import("@components/pages/parentTeacherWorkSpace/course-management/tabs/CatalogCourses"));
const CourseManagementCourseJourney = lazy(() => import("@components/pages/parentTeacherWorkSpace/course-management/CourseJourney"));
const CourseManagementCourseRequests = lazy(() => import("@components/pages/parentTeacherWorkSpace/course-management/CourseRequests"));
// StudentPortfolioView import removed per Chronicle boundary (2026-04-15).
// Component preserved at components/pages/parentTeacherWorkSpace/portfolio/
// as Chronicle incubator. Portfolio & Credentials is Chronicle-exclusive.

// AI-native grading screen (Snap & grade · Enter grades · Summary). Exposed as a
// "Grade" tab in the Work Space nav group below (parentTeacherNavigations), which
// both renders the sidebar tab and registers the /parent-teacher/grade route.
const Grading = lazy(() => import("@components/pages/parentTeacherWorkSpace/grading/GradingScreen"));
const PtTutorControls = lazy(() => import("@components/pages/parentTeacherWorkSpace/tutorControls/PtTutorControls"));
// Parent-facing AI Tutor dashboard (§6.1): the living map, mastery summary,
// locked early-warning, and recent alerts for a selected child. Sits alongside
// Tutor Controls (which is the SETUP surface: which book/course feeds the tutor).
const PtTutorDashboard = lazy(() => import("@components/pages/parentTeacherWorkSpace/tutorDashboard/PtTutorDashboard"));

const PT_TABS_UNLOCKED = import.meta.env.VITE_PT_UNLOCK_TABS === "true";

export const parentTeacherRoutes: NavigationRoute[] = [
  {
    index: true,
    label: "index",
    component: <Dashboard />,
  },
  {
    path: "profile",
    label: "profile",
    component: <Profile />,
  },
  {
    path: "profile/edit",
    label: "edit-profile",
    component: <EditProfile />,
  },
  {
    path: "profile/change-password",
    label: "change-password",
    component: <ChangePassword />,
  },
  {
    path: "student/:student_id/lesson/:lesson_id",
    label: "student lesson",
    component: <div> student / lesson </div>,
  },
  {
    label: "Dashboard",
    path: "dashboard/:widget",
    component: <DashboardWidget />,
  },
  {
    label: "Student Profile",
    path: "students/:student_id",
    component: <StudentProfile />,
  },
  {
    label: "Grade Book Details",
    path: "gradebooks/:student_id/:grade_level_id",
    component: <GradeBookDetailsRoute />,
    children: [
      {
        index: true,
        component: <GradeBookGradesTab />,
      },
      {
        path: "attendance-behavior",
        component: <GradeBookAttendancesTab />,
      }
    ]
  },
  {
    path: "course-management/requests",
    component: <Navigate to="for-approval" />,
  },
  {
    path: "course-management/catalogs/:catalog_id",
    label: "Catalog Courses",
    component: <CourseManagementCatalogCourses />,
  },
  {
    path: "course-management/:course_id/journey/:topic_id?/:lesson_id?/:activity_id?",
    label: "Course Journey",
    component: <CourseManagementCourseJourney />,
  },
  {
    path: "course-management/requests/:status",
    label: "Course Requests",
    component: <CourseManagementCourseRequests />,
  },
  // "students/:student_id/portfolio" route removed per Chronicle boundary
  // (2026-04-15). Becomes a Chronicle section feature when Chronicle is enabled.
];

export const parentTeacherNavigations: NavigationGroup[] = [
  {
    title: "Getting Started",
    routes: [
      {
        label: "dashboard",
        component: <Dashboard />,
        icon: <DashboardIcon fontSize="small" />,
        path: "dashboard",
      },
    ],
  },
  {
    title: "Work Space",
    routes: [
      {
        path: "students",
        label: "students",
        component: <Students />,
        icon: <ReadIcon fontSize="medium" />,
        hidden: !PT_TABS_UNLOCKED,
      },
      {
        path: "courses",
        label: "courses",
        component: <Courses />,
        icon: <MyCoursesIcon fontSize="medium" />,
        hidden: !PT_TABS_UNLOCKED,
        children: [
          {
            index: true,
            component: <CoursesCoursesTab />,
          },
          {
            path: ":course_id/journey/:student_id?/:topic_id?/:lesson_id?/:activity_id?",
            component: <CoursesJourneyTab />,
          },
        ],
      },
      {
        path: "calendar",
        label: "calendar",
        component: <Calendar />,
        icon: <MyPathIcon fontSize="medium" />,
        hidden: !PT_TABS_UNLOCKED,
      },
      {
        path: "assessments",
        label: "assessments",
        component: <Assessments />,
        icon: <MyAssessmentIcon fontSize="medium" />,
        hidden: true,
        children: [
          {
            index: true,
            component: <Navigate to="assigned" />,
          },
          {
            // "All" view (slide 138/141) — the Assigned listing without a
            // student pre-filter. The container renders the All <Tab> linking
            // here; this child makes the tab a working route, not a dead link.
            path: "all",
            component: <AssessmentsAssignedTab />,
          },
          {
            path: "assigned",
            component: <AssessmentsAssignedTab />,
          },
          {
            path: "graded",
            component: <AssessmentsGradedTab />,
          },
        ],
      },
      {
        path: "gradebooks",
        label: "grade books",
        // Hidden from the PT side menu (Family-Dashboard simplification: leaves
        // Dashboard + Grade). The route stays registered so a child's grade book
        // is still reachable by direct link / deep link, just not from the nav.
        hidden: !PT_TABS_UNLOCKED,
        // Family-dashboard model: the PT picks a child in the shared sidecar and
        // grade books opens THAT child's detail (GradeBooks redirects to the
        // per-student GradeBookDetails, which has its own per-student Submit — PTs
        // still never get a roster-level bulk Submit). The shared RoleAwareGradeBooks
        // roster stays for the teacher route only.
        component: <GradeBooks />,
        icon: <MyGradebookIcon fontSize="medium" />,
      },
      {
        // AI-native grading screen (Snap & grade · Enter grades · Summary).
        path: "grade",
        label: "grading",
        component: <Grading />,
        // ReportCardIcon hard-codes width/height 40x53 in its own sx; pass an sx
        // (spread last in the component, so it wins) to size it to 20x20 like the
        // other nav icons (e.g. DashboardIcon fontSize="small" → 20px).
        icon: <ReportCardIcon sx={{ width: 20, height: 20 }} />,
      },
      {
        // AI Tutor dashboard (§6.1): the parent-facing view of what the child is
        // learning with the tutor — living map, mastery summary, locked
        // early-warning, and recent alerts. READ-ONLY; no dialogue (teen covenant).
        path: "tutor",
        label: "Orbit",
        component: <PtTutorDashboard />,
        icon: <AutoAwesomeIcon sx={{ width: 20, height: 20 }} />,
        // Pre-release: hidden in prod, shown where the tutor backend is enabled.
        requiresAiTutor: true,
      },
      {
        // AI Tutor SETUP: which courses / vectorized textbooks feed each child's
        // tutor path (Chronicle-backed). Distinct from the dashboard above.
        path: "tutor-controls",
        label: "Orbit setup",
        component: <PtTutorControls />,
        icon: <AutoAwesomeIcon sx={{ width: 20, height: 20 }} />,
        // Pre-release: hidden in prod, shown where the tutor backend is enabled.
        requiresAiTutor: true,
      },
    ],
  },
  {
    title: "Manage Hub",
    routes: [
      {
        path: "course-management",
        label: "course management",
        component: <CourseManagement />,
        icon: <MyCoursesIcon fontSize="medium" />,
        hidden: true,
        children: [
          {
            index: true,
            component: <Navigate to="courses" />,
          },
          {
            path: "courses",
            label: "Teacher Courses",
            component: <CourseManagementTeacherCourses />,
          },
          {
            path: "catalogs",
            label: "Catalogs",
            component: <CourseManagementCatalogs />,
          },
          {
            path: "catalogs/:catalog_id",
            label: "Catalogs",
            component: <CourseManagementCatalogCourses />,
          },
          
        ],
      },
    ],
  },
];

export default parentTeacherRoutes;
